<template>
  <div class="p-6">
    <!-- Source row: CSV upload and the vaults to invite into, as labelled fields -->
    <div class="flex flex-col md:flex-row md:items-start border-b pb-6">
      <div class="md:w-64 md:flex-none">
        <label class="font-medium block">Invite from a file</label>
        <button
          type="button"
          class="mt-1 w-full h-10 border border-brand bg-white text-brand text-sm font-medium px-4 rounded hover:bg-brand hover:text-white"
          @click="$refs.upload.open()"
        >
          <i class="fa fa-upload mr-1"></i> Upload CSV
        </button>
      </div>
      <div class="flex-1 mt-4 md:mt-0 md:ml-4">
        <label class="font-medium block">Invite to vaults</label>
        <multiselect
          class="mt-1"
          v-model="selected_vaults"
          :options="vault_options"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          group-values="vaults"
          group-label="label"
          :group-select="true"
          placeholder="Select vaults"
          label="name"
          track-by="name"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <span class="multiselect__single" v-if="values.length &amp;&amp; !isOpen">
              {{ values.length === 1 ? values[0].name : values.length + " vaults selected" }}
            </span>
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Desktop: one row per invitation. The header uses the same widths as InviteRow. -->
    <div class="hidden md:block mt-4">
      <div class="flex flex-row items-end px-2 pb-2 border-b text-xxs uppercase tracking-wide text-grey-dark font-semibold">
        <div class="flex-1 min-w-0">Email</div>
        <div class="w-32 ml-2 flex-none">First name</div>
        <div class="w-32 ml-2 flex-none">Last name</div>
        <div class="w-32 ml-2 flex-none">Mobile</div>
        <div class="w-48 ml-2 flex-none">Permission profile</div>
        <div class="w-20 ml-2 flex-none">{{ unitLabel }}</div>
        <div class="w-10 ml-2 flex-none"></div>
      </div>

      <invite-row
        v-for="(invite, idx) in pending_invite"
        :key="idx"
        :invite="invite"
        :roles="roles"
        :index="idx"
        ref="invite_row"
        :invited-members="invited_members"
        @valid-data="validated"
        @remove-invite="removeInvite"
      ></invite-row>

      <div class="flex justify-end py-2">
        <button
          type="button"
          class="border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
          @click="addRow()"
        >
          <i class="fa fa-plus mr-1"></i> Add another
        </button>
      </div>

      <div class="flex items-center justify-between border-t pt-4">
        <p class="text-sm text-grey-darker">{{ readySentence }}</p>
        <button
          type="button"
          class="bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark"
          :class="{ 'cursor-not-allowed opacity-50': inviting }"
          :disabled="inviting"
          @click="validate()"
        >
          <span v-if="inviting"><i class="fa fa-spinner fa-spin mr-1"></i> Inviting</span>
          <span v-else><i class="fa fa-envelope mr-1"></i> Send invitations</span>
        </button>
      </div>
    </div>

    <!-- Mobile: one form, Add puts it on the pending list below -->
    <div class="block md:hidden">
      <invite-row-mobile
        :roles="roles"
        ref="invite_row_mobile"
        :count="count_invite"
        :unit-label="unitLabel"
        @invite-details="addToPendingInvites"
      ></invite-row-mobile>

      <button
        type="button"
        class="w-full border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded"
        @click.prevent="$refs.invite_row_mobile.getInviteDetails()"
      >
        <i class="fa fa-plus mr-1"></i> Add to list
      </button>

      <div class="mt-4 border-t">
        <div
          v-for="(invite, index) in pending_invite"
          :key="index"
          class="flex justify-between items-center border-b py-3"
          v-if="invite.email != ''"
        >
          <div class="min-w-0">
            <p class="truncate">{{ invite.first_name }} {{ invite.last_name }}</p>
            <p class="text-xs text-grey-darker truncate">{{ invite.email }}</p>
          </div>
          <button
            type="button"
            class="ml-2 flex-none border border-red bg-white text-red rounded py-2 px-3"
            @click.prevent="removeInvite(index)"
            aria-label="Remove"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <p class="text-sm text-grey-darker mt-4">{{ readySentence }}</p>
        <button
          type="button"
          class="w-full mt-2 bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded"
          :class="{ 'cursor-not-allowed opacity-50': inviting || count_invite === 0 }"
          :disabled="inviting || count_invite === 0"
          @click.prevent="
            inviting = true;
            sendInvitations();
          "
        >
          <span v-if="inviting"><i class="fa fa-spinner fa-spin mr-1"></i> Inviting</span>
          <span v-else><i class="fa fa-envelope mr-1"></i> Send invitations</span>
        </button>
      </div>
    </div>

    <sweet-modal overlay-theme="dark" ref="sending_invite" hide-close-button blocking>
      <div class="my-4">
        <p class="text-lg font-medium"><i class="fa fa-paper-plane mr-1"></i> Sending invitations</p>
        <p class="text-grey-darker mt-2"><i class="fa fa-circle-notch fa-spin mr-1"></i> Please wait.</p>
      </div>
    </sweet-modal>

    <sweet-modal icon="error" ref="sending_error" overlay-theme="dark" @close="onErrorModalClose">
      <p class="font-medium text-lg">The invitations could not be sent</p>
      <p v-if="invite_error_message" class="mt-2 text-grey-darker">{{ invite_error_message }}</p>
      <p v-else class="mt-2 text-grey-darker">Please try again or contact support.</p>
    </sweet-modal>

    <sweet-modal ref="upload" overlay-theme="dark" title="Invite from a CSV file" enable-mobile-fullscreen>
      <div class="text-left">
        <!-- Two steps, each a row: what to do on the left, one button of a fixed width on the right -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 border-b">
          <p class="text-sm">
            <span class="font-medium">1. Download the template</span>
            <span class="block text-grey-darker">Fill in one person per line and save it as CSV.</span>
          </p>
          <a
            href="/download/invitation-csv-template"
            class="mt-2 sm:mt-0 sm:ml-4 w-64 flex-none text-center whitespace-no-wrap border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded no-underline hover:bg-brand hover:text-white"
          >
            <i class="fa fa-download mr-1"></i> Download template
          </a>
        </div>

        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3" ref="drop_target">
          <p class="text-sm min-w-0">
            <span class="font-medium">2. Upload the completed file</span>
            <span class="block text-grey-darker truncate" v-if="pluploader_object && pluploader_object.files.length > 0">
              {{ pluploader_object.files[0].name }}
            </span>
            <span class="block text-grey-darker" v-else>No file chosen yet.</span>
          </p>
          <button
            type="button"
            class="mt-2 sm:mt-0 sm:ml-4 w-64 flex-none whitespace-no-wrap border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
            ref="browse_files"
          >
            <i class="fa fa-file mr-1"></i> Choose CSV file
          </button>
        </div>

        <div v-if="pluploader_object && pluploader_object.files.length > 0" class="border-t pt-3">
          <div class="h-2 bg-grey-light rounded">
            <div class="h-2 bg-brand rounded" :style="'width:' + pluploader_object.files[0].percent + '%;'"></div>
          </div>
          <div class="flex justify-end mt-3">
            <button
              type="button"
              class="border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded hover:bg-red hover:text-white"
              @click="removeFile(pluploader_object.files[0])"
            >
              Remove
            </button>
            <button
              type="button"
              class="ml-2 bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark"
              @click="upload"
            >
              <i class="fa fa-upload mr-1"></i> Upload
            </button>
          </div>
        </div>
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import InviteRow from "../../components/Invitations/InviteRow";
import InviteRowMobile from "../../components/Invitations/InviteRowMobile";
import { withoutUserARole } from "../../vault_roles.mjs";
import { mapGetters } from "vuex";

export default {
  components: { InviteRow, InviteRowMobile },

  data() {
    return {
      pluploader_object: null,

      pending_invite: [],
      roles: [],
      inviting: false,

      invited_members: [],

      custom_header: {
        title: ["User A"],
      },
      selected_vaults: [],
      invite_error_message: "",
    };
  },

  mounted() {
    window.addEventListener('error', (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
      }
    });

    this.initializeUploader();
    this.pending_invite.push({
      email: "",
      title: "",
      first_name: "",
      last_name: "",
      unit_number: "",
      invitation_message_template_id: 1,
      role: {
        id: "",
      },
      start_date: "",
      expiry_days: "",
      is_valid: false,
    });

    this.selected_vaults = this.vaults.filter(x => x.id == this.$route.params.vault_id);
    this.getRoles();
    this.getPendingInvitations();
    this.getInvitedMembers();
  },

  methods: {
    validate() {
      this.inviting = true;

      // Wait for every row's duplicate check instead of a fixed timer. The old
      // 2 s timer dropped invites when the check was slow, or when the user
      // left the page first (GLOBAL-VAULTS-1ZY).
      const rows = this.$refs.invite_row || [];
      // A failed check still sends the rows that did pass, as before.
      Promise.all(rows.map((row) => row.validateAndGet(this.selected_vaults)))
        .catch((error) => console.error("Invite validation failed:", error))
        .then(() => this.sendInvitations());
    },

    validated(payload) {
      this.$set(this.pending_invite, payload.index, payload.data);
    },

    sendInvitations() {
      let valid_invite_count = _.filter(this.pending_invite, function (member) {
        if (member.is_valid) return member;
      }).length;
      
      if (valid_invite_count > 0) {
        this.$refs.sending_invite?.open();
        let t = this;
        
        let members = this.pending_invite;
        let selectedVaults = this.selected_vaults;
        
        // Build one bulk-invitations request per vault (single POST per vault, no separate commit)
        let bulkPromises = selectedVaults.map((vault) => {
          let invitations = [];
          _.forEach(members, function (member) {
            if (!member.is_valid) return;
            let role = vault.roles && vault.roles.find((r) => r.title == member.role.title);
            if (!role) return;
            invitations.push({
              email: member.email,
              title: member.title || '',
              first_name: member.first_name,
              last_name: member.last_name,
              unit_number: member.unit_number,
              role_id: role.id,
              expiry_days: member.expiry_days ? moment(member.expiry_days, "DD/MM/YYYY HH:mm A").format("YYYY-MM-DD HH:mm:ss") : null,
              start_date: member.start_date ? moment(member.start_date, "DD/MM/YYYY HH:mm A").format("YYYY-MM-DD HH:mm:ss") : null,
              mobile_number: member.mobile_number || null,
            });
          });
          if (invitations.length === 0) return Promise.resolve();
          return axios.post(`api/vaults/${vault.id}/invitations/bulk-invitations`, { invitations });
        });
        
        Promise.all(bulkPromises)
          .then((responses) => {
            const invitationIds = _.uniq(_.flatten(_.map(_.filter(responses, (r) => r && r.data), (r) => (r.data.invitations || []).map((inv) => inv.id))));
            const done = () => {
              this.$refs.sending_invite?.close();
              this.$noty.success("Invitation/s has been sent!", {
                killer: true,
                timeout: 2000,
              });
              let remaining = _.filter(t.pending_invite, function (member) {
                return member.is_valid == false;
              });
              t.pending_invite = remaining;
              this.getInvitedMembers();
              this.inviting = false;
            };
            if (invitationIds.length > 0) {
              return axios.post('api/invitations/commit-and-send-consolidated', { invitation_ids: invitationIds }).then(done);
            }
            done();
          })
          .catch((error) => {
            console.error('Error during bulk invitation:', error);
            const data = error.response?.data;
            if (data?.errors && typeof data.errors === 'object') {
              const list = _.uniq(_.flatten(Object.values(data.errors)));
              this.invite_error_message = list.join(' ');
            } else {
              this.invite_error_message = data?.message || (error.response?.status ? `Request failed (${error.response.status})` : error.message) || 'Please try again or contact support.';
            }
            this.$refs.sending_error?.open();
            this.inviting = false;
          });
      } else {
        this.inviting = false;
      }
    },
    onErrorModalClose() {
      if (this.$refs.sending_invite) {
        this.$refs.sending_invite.close();
      }
    },
    getRoles() {
      axios
        .get(`api/vaults/${this.$route.params.vault_id}/roles`)
        .then(({ data }) => {
          if (window.vault_type == "business") {
            _.each(data, (datum, key) => {
              let role = this.sanitize(datum);
              data[key]["role"] = role;
            });

            this.roles = data;
          }

          if (window.vault_type == "strata") {
            this.roles = data;
          }
          this.customizeRole();
        });
    },
    sanitize(role) {
      let vault_id = this.current_vault.id;
      let role_name = role.name;
      let replace_char = vault_id + "-";
      let Role = _.trim(role_name.replace(replace_char, " "));

      let index = _.findIndex(this.custom_header.title, (t) => {
        return _.trim(t) == _.trim(Role);
      });

      if (index == -1) {
        return Role;
      } else {
        return role.title.replace(/.+?(\- )/g, "");
      }
    },
    addRow() {
      this.pending_invite.push({
        email: "",
        title: "",
        first_name: "",
        last_name: "",
        unit_number: "",
        invitation_message_template_id: 1,
        role_id: "",
        mobile_number: "",
        is_valid: false,
      });
    },
    initializeUploader() {
      this.pluploader_object = new plupload.Uploader({
        browse_button: this.$refs.browse_files,
        url: `upload/parse-email-csv`,
        chunk_size: "24mb",
        file_data_name: "email_csv",
        multi_selection: false,
        filters: {
          mime_types: [
            {
              title: "Files",
              extensions: "csv",
            },
          ],
        },
        multipart_params: {
          _token: Laravel.csrfToken,
        },
      });

      this.pluploader_object.bind("FileUploaded", (up, file, parsed_data) => {
        let data;
        try {
          // The response is not always JSON: a session timeout answers with the
          // login page, and a failing gateway can answer with an empty body.
          data = JSON.parse(parsed_data.response);
        } catch (e) {
          this.$noty.error(
            "The uploaded file could not be read. Please refresh the page and try again.",
            { killer: true, timeout: 4000 }
          );
          this.initializeUploader();
          return;
        }

        this.pending_invite = [];
        let roles = this.roles;
        let vm = this;
        _.each(data, (datum) => {
          let parsed_role = datum.role_id;
          let role = this.matchRole(parsed_role);

          if (role) {
            datum.role = role;
          } else {
            datum.role = "";
          }
          vm.pending_invite.push(datum);
        });
        this.$refs.upload.close();
        this.$noty.success("File has been uploaded and parsed", {
          killer: true,
          timeout: 2000,
        });
        this.initializeUploader();
      });

      this.pluploader_object.bind("FilesAdded", (up, files) => {
        while (up.files.length > 1) {
          up.removeFile(up.files[0]);
        }
      });

      this.pluploader_object.init();
    },
    upload() {
      this.pluploader_object.start();
    },
    removeFile(file) {
      this.pluploader_object.removeFile(file);
    },
    getPendingInvitations() {
      axios
        .get(`api/vaults/${this.$route.params.vault_id}/invitations`, {
          params: {
            show_pending: 1,
          },
        })
        .then(({ data }) => {
          if (data.length >= 1) {
            this.pending_invite = data;
          }
        });
    },
    removeInvite(payload) {
      this.pending_invite.splice(payload, 1);
    },
    addToPendingInvites(payload) {
      this.pending_invite = _.filter(this.pending_invite, function (invite) {
        return invite.email != "";
      });

      this.pending_invite.push(payload);

      this.$refs.invite_row_mobile.resetForm();
    },
    matchRole(r) {
      let end_str_role = _.lowerCase(r).substr(_.lowerCase(r).length - 1);
      let role = _.find(this.roles, (role) => {
        return _.endsWith(_.toLower(role.name), end_str_role);
      });

      if (role) {
        return role;
      }

      return false;
    },
    // Single place that drops the "User A" role, so the missing-role guard
    // cannot drift between callers again (GLOBAL-VAULTS-204).
    removeUserARole() {
      this.roles = withoutUserARole(this.roles, this.current_vault?.id);
    },
    customizeRole() {
      this.removeUserARole();
    },
    getInvitedMembers() {
      axios
        .get(`api/vaults/${this.$route.params.vault_id}/invitations`, {
          params: {
            show_pending: 0,
          },
        })
        .then(({ data }) => {
          if (!_.isEmpty(data)) {
            this.invited_members = data;
          }
        });
    },
  },

  computed: {
    count_invite() {
      let valid_invite = _.filter(this.pending_invite, function (invite) {
        return invite.email != "";
      });

      return valid_invite.length;
    },

    readySentence() {
      const n = this.count_invite;
      if (n === 0) return "Add at least one person";
      return n === 1 ? "1 invitation ready" : `${n} invitations ready`;
    },

    // The last column is the lot or unit number on strata vaults and a job
    // role on business vaults. The field is unit_number either way.
    unitLabel() {
      return window.vault_type === "strata" ? "Unit" : "Role";
    },
    ...mapGetters({
      role: "getUserRole",
      current_vault: "getCurrentVault",
      vaults: "getVaults",
      userRoles: "getRoles",
      vaultsWithRoles: "getVaultsWithRole",
    }),
    vault_type() {
      return window.vault_type;
    },
    vault_options() {
      let vaultsWithARole = this.vaultsWithRoles.filter((vault) => {
        if (!vault.role) {
          return false;
        }

        return vault.role.name.split("User ")[1] == "A";
      });

      return [
        {
          label: "Select All",
          vaults: vaultsWithARole,
        },
      ];
    },
  },

  watch: {
    role() {
      if (this.role == "userB") {
        this.removeUserARole();
      }
    },

    // On a hard reload the vault list lands after mount, so the preselection
    // in mounted() found nothing. The vault_options watcher below only covers
    // vaults where the user is User A, so a User B ended up with no vault
    // selected and Send sent nothing.
    vaults(newVal) {
      if (this.selected_vaults.length === 0 && Array.isArray(newVal)) {
        this.selected_vaults = newVal.filter((x) => x.id == this.$route.params.vault_id);
      }
    },

    vault_options(newVal) {
      if (
        this.selected_vaults.find(
          (vault) => vault.id == this.$route.params.vault_id
        )
      ) {
        return;
      }

      if (newVal[0].vaults.length > 0) {
        let openVault = newVal[0].vaults.find((vault) => {
          return vault.id == this.$route.params.vault_id;
        });

        if (openVault) {
          this.selected_vaults.push(openVault);
        }
      }
    },
  },
};
</script>

<style lang="scss">
.permission_role {
  .multiselect__tags {
    min-height: 0px !important;
    display: block !important;
    padding: 6px 40px 0 8px !important;
    border-radius: 5px !important;
    border: 1px solid #d8d8d8 !important;
    background: #fff !important;
  }
  //   .invalid > .multiselect__tags {
  //     border: 1px solid #e3342f !important;
  //   }
  .multiselect__input {
    font-size: 16px !important;
  }
}
</style>
